import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { termsContentEN } from '../content/terms-en';
import { termsContentET } from '../content/terms-et';
import { termsContentAR } from '../content/terms-ar';
import TermsContentRussian from './TermsContentRussian';

const TermsOfService = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language;
  
  // Get content based on language
  const getContentByLang = () => {
    switch(currentLang) {
      case 'en': return termsContentEN;
      case 'et': return termsContentET;
      case 'ar': return termsContentAR;
      default: return null;
    }
  };
  
  const content = getContentByLang();
  
  // Translations for page elements
  const translations = {
    ru: {
      title: 'Условия оказания услуг',
      subtitle: 'Последнее обновление: 6 ноября 2025',
      backButton: 'Вернуться на главную',
      tableOfContents: 'Содержание',
      important: 'Важно',
      contact: 'Контакты:'
    },
    en: {
      title: 'Terms of Service',
      subtitle: 'Last updated: 6 November 2025',
      backButton: 'Back to main page',
      tableOfContents: 'Table of Contents',
      important: 'Important',
      contact: 'Contact:'
    },
    et: {
      title: 'Teenuse osutamise tingimused',
      subtitle: 'Uuendatud: 6. november 2025',
      backButton: 'Tagasi avalehele',
      tableOfContents: 'Sisukord',
      important: 'Tähtis',
      contact: 'Kontakt:'
    },
    ar: {
      title: 'شروط الخدمة',
      subtitle: 'آخر تحديث: 6 نوفمبر 2025',
      backButton: 'العودة إلى الصفحة الرئيسية',
      tableOfContents: 'جدول المحتويات',
      important: 'مهم',
      contact: 'اتصل بنا:'
    }
  };
  
  const lang = translations[currentLang] || translations['ru'];
  
  // Use content sections if available, otherwise use Russian sections
  const sections = content?.sections || [
    { id: 'general', title: '1. Общие положения' },
    { id: 'services', title: '2. Предмет и приёмка услуг' },
    { id: 'reporting', title: '3. Оценка, отчётность и результат' },
    { id: 'payment', title: '4. Оплата и пени' },
    { id: 'obligations', title: '5. Права и обязанности сторон' },
    { id: 'ip', title: '6. Интеллектуальная собственность' },
    { id: 'confidentiality', title: '7. Конфиденциальность и обработка данных' },
    { id: 'liability', title: '8. Платежи, возвраты и ответственность' },
    { id: 'termination', title: '9. Срок действия, расторжение и последствия' },
    { id: 'force-majeure', title: '10. Форс-мажор' },
    { id: 'disputes', title: '11. Разрешение споров и применимое право' },
    { id: 'other', title: '12. Прочие положения' }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-gray-800 py-6 px-4 sticky top-0 bg-black/95 backdrop-blur-sm z-10">
        <div className="max-w-6xl mx-auto">
          <a 
            href={currentLang === 'et' ? '/' : `/${currentLang}`}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            {lang.backButton}
          </a>
          <div className="flex items-center gap-3">
            <FileText className="w-10 h-10 text-blue-400" />
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white text-left rtl:text-right">
                {lang.title}
              </h1>
              <p className="text-gray-400 text-sm mt-1 text-left rtl:text-right">
                {lang.subtitle}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <aside className="lg:col-span-1">
            <div className="sticky top-32">
              <h3 className="text-sm font-semibold text-gray-400 uppercase mb-4 text-left rtl:text-right">{lang.tableOfContents}</h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="block text-sm text-gray-400 hover:text-white transition-colors py-1"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-12"
            >
              {/* Company Info */}
              <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-2xl p-6">
                <h2 className="text-xl font-bold text-white mb-3">Polyus Plus OÜ</h2>
                <div className="space-y-1 text-gray-300 text-left rtl:text-right">
                  <p>
                    {currentLang === 'ru' && 'Регистрационный код: '}
                    {currentLang === 'en' && 'Registration code: '}
                    {currentLang === 'et' && 'Registrikood: '}
                    {currentLang === 'ar' && 'رقم التسجيل: '}
                    14890902
                  </p>
                  <p>
                    {currentLang === 'ru' && 'Адрес: '}
                    {currentLang === 'en' && 'Address: '}
                    {currentLang === 'et' && 'Aadress: '}
                    {currentLang === 'ar' && 'العنوان: '}
                    Uuslinna 11, Tallinn, 11415, {currentLang === 'ru' ? 'Эстония' : 'Estonia'}
                  </p>
                  <p>Email: <a href="mailto:info@polyusplus.com" className="text-blue-400 hover:underline">info@polyusplus.com</a></p>
                </div>
              </div>

              {/* Language Notice - removed as full translations are now available */}

              {/* Important Notice */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6 flex gap-4">
                <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-yellow-400 mb-2 text-left rtl:text-right">{lang.important}</h3>
                  <p className="text-gray-300 leading-relaxed text-left rtl:text-right">
                    {currentLang === 'ru' && 'Оплатой счёта или подписанием Договора Клиент подтверждает, что ознакомился с настоящими Условиями и принимает их полностью. Настоящие Условия применяются исключительно к сделкам между юридическими лицами (B2B).'}
                    {content?.importantNotice || (currentLang === 'en' && 'By signing the Agreement or paying the invoice, the Client confirms that they have read, understood, and accepted these Terms in full. These Terms apply only to B2B transactions between legal entities and/or sole proprietors.')}
                  </p>
                </div>
              </div>
              
              {/* Render content for EN/ET/AR */}
              {content && currentLang !== 'ru' && (
                <>
                  {/* Section 1 - General */}
                  <section id="general" className="scroll-mt-32">
                    <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
                      {content.general.title}
                    </h2>
                    <div className="space-y-4 text-gray-300 leading-relaxed">
                      {content.general.content.map((item, idx) => (
                        <p key={idx}><strong className="text-white">{item.num}</strong> {item.text}</p>
                      ))}
                    </div>
                  </section>

                  {/* Section 2 - Services */}
                  <section id="services" className="scroll-mt-32">
                    <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
                      {content.services.title}
                    </h2>
                    <div className="space-y-4 text-gray-300 leading-relaxed">
                      {content.services.content.map((item, idx) => (
                        <p key={idx}>
                          <strong className="text-white">{item.num}</strong> 
                          {item.highlight ? <strong className="text-blue-400"> {item.text}</strong> : ` ${item.text}`}
                        </p>
                      ))}
                    </div>
                  </section>

                  {/* Section 3 - Reporting */}
                  <section id="reporting" className="scroll-mt-32">
                    <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
                      {content.reporting.title}
                    </h2>
                    <div className="space-y-4 text-gray-300 leading-relaxed">
                      {content.reporting.content.map((item, idx) => (
                        <p key={idx}><strong className="text-white">{item.num}</strong> {item.text}</p>
                      ))}
                    </div>
                  </section>

                  {/* Section 4 - Payment */}
                  <section id="payment" className="scroll-mt-32">
                    <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
                      {content.payment.title}
                    </h2>
                    <div className="space-y-4 text-gray-300 leading-relaxed">
                      {content.payment.content.map((item, idx) => (
                        <p key={idx}>
                          <strong className="text-white">{item.num}</strong> {item.text}
                          {item.penalty && <strong className="text-red-400"> {item.penalty}</strong>}
                        </p>
                      ))}
                    </div>
                  </section>

                  {/* Section 5 - Obligations */}
                  <section id="obligations" className="scroll-mt-32">
                    <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
                      {content.obligations.title}
                    </h2>
                    <div className="space-y-4 text-gray-300 leading-relaxed">
                      {content.obligations.content.map((item, idx) => (
                        <p key={idx}><strong className="text-white">{item.num}</strong> {item.text}</p>
                      ))}
                    </div>
                  </section>

                  {/* Section 6 - IP */}
                  <section id="ip" className="scroll-mt-32">
                    <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
                      {content.ip.title}
                    </h2>
                    <div className="space-y-4 text-gray-300 leading-relaxed">
                      {content.ip.content.map((item, idx) => (
                        <p key={idx}><strong className="text-white">{item.num}</strong> {item.text}</p>
                      ))}
                    </div>
                  </section>

                  {/* Section 7 - Confidentiality */}
                  <section id="confidentiality" className="scroll-mt-32">
                    <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
                      {content.confidentiality.title}
                    </h2>
                    <div className="space-y-4 text-gray-300 leading-relaxed">
                      {content.confidentiality.content.map((item, idx) => (
                        <p key={idx}><strong className="text-white">{item.num}</strong> {item.text}</p>
                      ))}
                    </div>
                  </section>

                  {/* Section 8 - Liability */}
                  <section id="liability" className="scroll-mt-32">
                    <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
                      {content.liability.title}
                    </h2>
                    <div className={`space-y-4 text-gray-300 leading-relaxed ${content.liability.isHighRisk ? 'bg-red-500/5 border border-red-500/20 rounded-xl p-6' : ''}`}>
                      {content.liability.content.map((item, idx) => (
                        <p key={idx}>
                          <strong className="text-white">{item.num}</strong> {item.text}
                          {item.amount && <strong className="text-red-400"> {item.amount}</strong>}
                        </p>
                      ))}
                    </div>
                  </section>

                  {/* Section 9 - Termination */}
                  <section id="termination" className="scroll-mt-32">
                    <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
                      {content.termination.title}
                    </h2>
                    <div className="space-y-4 text-gray-300 leading-relaxed">
                      {content.termination.content.map((item, idx) => (
                        <p key={idx}><strong className="text-white">{item.num}</strong> {item.text}</p>
                      ))}
                    </div>
                  </section>

                  {/* Section 10 - Force Majeure */}
                  <section id="force-majeure" className="scroll-mt-32">
                    <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
                      {content.forceMajeure.title}
                    </h2>
                    <div className="space-y-4 text-gray-300 leading-relaxed">
                      {content.forceMajeure.content.map((item, idx) => (
                        <p key={idx}><strong className="text-white">{item.num}</strong> {item.text}</p>
                      ))}
                    </div>
                  </section>

                  {/* Section 11 - Disputes */}
                  <section id="disputes" className="scroll-mt-32">
                    <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
                      {content.disputes.title}
                    </h2>
                    <div className="space-y-4 text-gray-300 leading-relaxed">
                      {content.disputes.content.map((item, idx) => (
                        <p key={idx}><strong className="text-white">{item.num}</strong> {item.text}</p>
                      ))}
                    </div>
                  </section>

                  {/* Section 12 - Other */}
                  <section id="other" className="scroll-mt-32">
                    <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800">
                      {content.other.title}
                    </h2>
                    <div className="space-y-4 text-gray-300 leading-relaxed">
                      {content.other.content.map((item, idx) => (
                        <p key={idx}>
                          <strong className="text-white">{item.num}</strong> {item.text}
                          {item.penalty && <strong className="text-red-400"> {item.penalty}</strong>}
                        </p>
                      ))}
                    </div>
                  </section>
                </>
              )}

              {/* Russian content */}
              {currentLang === 'ru' && <TermsContentRussian />}

              {/* Contact Section */}
              <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 border border-gray-700 rounded-2xl p-8 mt-12">
                <h3 className="text-2xl font-bold text-white mb-4 text-left rtl:text-right">
                  {currentLang === 'ru' && 'Контакты'}
                  {currentLang === 'en' && 'Contact'}
                  {currentLang === 'et' && 'Kontakt'}
                  {currentLang === 'ar' && 'اتصل بنا'}
                </h3>
                <div className="space-y-2 text-gray-300 text-left rtl:text-right">
                  <p className="text-xl font-semibold text-white">Polyus Plus OÜ</p>
                  <p>
                    {currentLang === 'ru' && 'Регистрационный код: '}
                    {currentLang === 'en' && 'Registration code: '}
                    {currentLang === 'et' && 'Registrikood: '}
                    {currentLang === 'ar' && 'رقم التسجيل: '}
                    14890902
                  </p>
                  <p>
                    {currentLang === 'ru' && 'Адрес: '}
                    {currentLang === 'en' && 'Address: '}
                    {currentLang === 'et' && 'Aadress: '}
                    {currentLang === 'ar' && 'العنوان: '}
                    Uuslinna 11, Tallinn 11415, Estonia
                  </p>
                  <p>Email: <a href="mailto:info@polyusplus.com" className="text-blue-400 hover:underline">info@polyusplus.com</a></p>
                  <a href="/#contact" className="inline-block mt-4 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:shadow-lg transition-all font-semibold">
                    {currentLang === 'ru' && 'Связаться с нами →'}
                    {currentLang === 'en' && 'Contact us →'}
                    {currentLang === 'et' && 'Võta ühendust →'}
                    {currentLang === 'ar' && 'اتصل بنا ←'}
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
